import { useEffect, useRef, type ReactNode } from 'react';
import { Button } from '@/components/ui/button';

interface LoadMoreProps {
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  /** What to show while the next page is loading (e.g. skeleton rows). */
  loadingContent?: ReactNode;
  /** Rendered once everything has been loaded (e.g. an "all caught up" note). */
  endContent?: ReactNode;
}

/**
 * Shared "keep scrolling" plumbing for paginated feeds: an
 * IntersectionObserver sentinel that auto-fetches the next page as it
 * approaches the viewport, with an explicit "Load more" button as the
 * fallback (and for browsers where the observer never fires). When
 * there's nothing left, `endContent` takes over.
 */
export function LoadMore({ hasNextPage, isFetchingNextPage, fetchNextPage, loadingContent, endContent }: LoadMoreProps) {
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting) && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { rootMargin: '600px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  if (!hasNextPage) return endContent ? <>{endContent}</> : null;

  return (
    <div ref={sentinelRef} className="py-4">
      {isFetchingNextPage ? (
        loadingContent
      ) : (
        <div className="flex justify-center">
          <Button variant="outline" size="sm" onClick={() => fetchNextPage()}>
            Load more
          </Button>
        </div>
      )}
    </div>
  );
}
